import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Stack,
} from "@mui/material";
import { useState } from "react";

import { OneflowTemplateAutocomplete } from "@/common/components/Integrations/Oneflow/OneflowTemplateAutocomplete";
import AppTypography from "@/common/components/Text/AppTypography";
import { OneflowHelper } from "@/common/helpers/integrations/oneflow";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserProfileV2 } from "@/common/hooks/user/useUserProfileV2";
import { ValidationHelper } from "@/common/validation";
import { appCommonConfig } from "@/config/config";
import { apiClient } from "@/core/api/ApiClient";
import { ContractDto, ContractOneflowContractCreateDto } from "@/core/api/generated/v1";

import AppButton from "../../../Button/AppButton";
import ContractLink from "../ContractLink";

export interface ContractCreateOneflowContractProps {
  contract: ContractDto;
  onCancel: () => void;
  onSave: (newValue: ContractDto) => void;
}

export function ContractCreateOneflowContract({
  contract,
  onCancel,
  onSave,
}: ContractCreateOneflowContractProps) {
  const { enqueueSnackbar } = useAppSnackbar();
  const profile = useUserProfileV2();

  const [createDto, setCreateDto] = useState<ContractOneflowContractCreateDto | null>({
    isIncludeCounterparty: true,
    isIncludeDataFields: true,
    isIncludeExpandedPdf: true,
    isIncludeCheckOutLocationDataField: true,
  });

  const oneflowDefaultsRequest = useApiRequest(
    apiClient.oneflowApi.apiV1ProvidersOneflowDefaultsGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
    },
    {
      deps: [],
    },
  );
  const oneflowDefaults = oneflowDefaultsRequest.data;
  if (oneflowDefaults?.defaultTemplateId && !createDto?.oneflowTemplateId) {
    setCreateDto({ ...createDto, oneflowTemplateId: oneflowDefaults.defaultTemplateId });
  }

  const isIniting = !oneflowDefaults;

  const create = async () => {
    try {
      const result =
        await apiClient.contractsApi.apiV1ContractsContractIdIntegrationsOneflowContractCreatePost({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          contractId: contract.id || "",
          contractOneflowContractCreateDto: {
            ...createDto,
          },
        });
      enqueueSnackbar("Oneflow contract created.", { variant: "success" });
      onSave(result.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  };

  if (!OneflowHelper.isFeatureEnabledForTenantUser(profile.profile)) {
    return null;
  }

  if (isIniting) {
    <LinearProgress />;
  }

  return (
    <Stack gap={1}>
      {contract.oneflowContract && (
        <Alert severity='error'>Oneflow contract is already created.</Alert>
      )}

      {!contract.oneflowContract && (
        <Stack gap={1}>
          <Alert severity='info'>
            <Box>
              Your are going to create a Oneflow contract from {appCommonConfig.appName} contract{" "}
              <ContractLink entityId={contract.id} entity={contract} />.
            </Box>
            <Box>Once submitted, a new contract in draft state should appear in Oneflow.</Box>
          </Alert>

          <Box>
            <OneflowTemplateAutocomplete
              templateId={createDto?.oneflowTemplateId}
              isPreload
              isAutoSelectSingleOption
              textFieldProps={{
                margin: "normal",
              }}
              onChange={(newValue) => {
                setCreateDto({ ...createDto, oneflowTemplateId: newValue?.id ?? null });
              }}
            />

            <FormGroup row>
              <FormControlLabel
                label='Include customer counterparty'
                control={
                  <Checkbox
                    disabled={!createDto?.oneflowTemplateId}
                    checked={createDto?.isIncludeCounterparty ?? false}
                    onChange={(e) =>
                      setCreateDto({ ...createDto, isIncludeCounterparty: e.target.checked })
                    }
                  />
                }
              />
            </FormGroup>

            <FormGroup row>
              <FormControlLabel
                label='Include expanded PDF (vehicle actual state PDF)'
                control={
                  <Checkbox
                    disabled={!createDto?.oneflowTemplateId}
                    checked={createDto?.isIncludeExpandedPdf ?? false}
                    onChange={(e) =>
                      setCreateDto({ ...createDto, isIncludeExpandedPdf: e.target.checked })
                    }
                  />
                }
              />
            </FormGroup>

            <FormGroup row>
              <FormControlLabel
                label={
                  <AppTypography
                    decoration={{ variant: "helpText" }}
                    tooltipProps={{
                      title: "Don't include data fields if you want to enter fields manually.",
                      isDefaultEnterDelays: true,
                    }}
                  >
                    Include data fields
                  </AppTypography>
                }
                control={
                  <Checkbox
                    disabled={!createDto?.oneflowTemplateId}
                    checked={createDto?.isIncludeDataFields ?? false}
                    onChange={(e) =>
                      setCreateDto({ ...createDto, isIncludeDataFields: e.target.checked })
                    }
                  />
                }
              />
            </FormGroup>

            <FormGroup row>
              <FormControlLabel
                label='Include check-out location data field'
                control={
                  <Checkbox
                    disabled={!createDto?.oneflowTemplateId}
                    checked={createDto?.isIncludeCheckOutLocationDataField ?? false}
                    onChange={(e) =>
                      setCreateDto({
                        ...createDto,
                        isIncludeCheckOutLocationDataField: e.target.checked,
                      })
                    }
                  />
                }
              />
            </FormGroup>
          </Box>

          {/* Primary actions */}
          <Stack
            direction={{ xxs: "column", sm: "row" }}
            gap={1}
            sx={{
              justifyContent: { xxs: "flex-start", sm: "flex-end" },
              alignItems: { xxs: "flex-start", sm: "flex-start" },
            }}
          >
            <AppButton
              variant='outlined'
              size='medium'
              color='text'
              onClick={() => {
                onCancel();
              }}
            >
              Cancel
            </AppButton>

            <AppButton variant='contained' size='medium' color='primary' onClick={create}>
              Create
            </AppButton>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
